import React, { useLayoutEffect, useMemo, useEffect, useState } from 'react';
import { ThemeProvider as EmotionThemeProvider, Theme, CacheProvider as EmotionCacheProvider } from '@emotion/react';
import { FloatingTree } from '@floating-ui/react-dom-interactions';
import { theme, type WeaveTheme } from '@frontend/theme';
import { usePrefersReducedMotion } from '../hooks/use-prefers-reduced-motion';
import { ThemeOptionProvider } from '../theme-option-provider';
import { createPrioritizedEmotionCache } from './emotion-linaria-precedence';

const emotionCache = createPrioritizedEmotionCache('frontend');

type ThemeProviderProps = {
  children?: React.ReactNode;
  includeEmotionTheme?: boolean;
  skipAnimation?: boolean;
  heightOffset?: number;
};

// Define these in the global scope because theme and root styles
// are consistent instances.

declare module '@emotion/react' {
  export interface Theme extends WeaveTheme {
    heightOffset: 0;
  }
}

export const WEAVE_FONT_SCALE_KEY = 'weave-app-font-scale';

export function useFontScale() {
  const [scale] = useState(() => {
    const saved = localStorage.getItem(WEAVE_FONT_SCALE_KEY);
    return saved ? parseFloat(saved) : null;
  });

  useEffect(() => {
    if (!scale) return;
    document.documentElement.style.fontSize = `${scale * 100}%`;
  }, [scale]);
}

export const ThemeProvider = ({
  children,
  includeEmotionTheme = false,
  skipAnimation = false,
  heightOffset,
}: ThemeProviderProps) => {
  useLayoutEffect(() => {
    document.body.setAttribute('data-theme', 'light');
  }, []);

  useFontScale();
  const prefersReducedMotion = usePrefersReducedMotion();

  const adjustedTheme = useMemo(() => {
    if (heightOffset) {
      return {
        ...theme,
        heightOffset,
      };
    }

    return theme;
  }, [heightOffset]);

  return (
    <ThemeOptionProvider skipAnimation={prefersReducedMotion || skipAnimation}>
      <FloatingTree>
        <EmotionCacheProvider value={emotionCache}>
          {includeEmotionTheme ? (
            <EmotionThemeProvider theme={adjustedTheme as Theme}>{children}</EmotionThemeProvider>
          ) : (
            <>{children}</>
          )}
        </EmotionCacheProvider>
      </FloatingTree>
    </ThemeOptionProvider>
  );
};
